// The Admin workspace (2026-10-01; before that a single "Admin" sidebar tab,
// Phase 5 of the multi-user plan, decision #7's "basic admin screen"). One
// left-hand tab per section, each rendered on first open:
//
//   Users & teams     groups + users: create/deactivate/reactivate/change
//                     role/teams/group (routers/admin.py) — org admin+, their
//                     own organisation only
//   Audit log         recent activity (routers/audit.py, filterable by user/
//                     action) — org admin+, own organisation's rows only
//   Data access       grants, councils, matrix, shadow/denied log
//                     (dataAccessUI.ts, routers/data_access.py) — platform_admin
//   Data maintenance  scheduler + tasks, alerts, suggested re-runs, data
//                     sources, GIS layer caches, fetch activity
//                     (maintenanceUI.ts, routers/maintenance.py) — platform_admin
//   Diagnostics       process memory / caches, serving bundle, Office engines,
//                     job worker (diagnosticsUI.ts) — platform_admin
//
// A tab the user may not use is hidden (display: none), not disabled — the
// backend refuses its routes anyway (403, or 404 for a GIS-view
// organisation), same "don't show a button that would just error"
// convention as panel.ts's canConfigure gating. workspaces.ts puts these
// tabs in the Admin workspace (ADMIN_TABS) and remembers the last one.
import { renderContactDirectorySection } from "./contactDirectoryUI";
import {
  ApiError,
  createAdminGroup,
  createAdminUser,
  deleteAdminGroup,
  getAuditLog,
  getTermsAcceptances,
  listAdminGroups,
  listAdminUsers,
  renameAdminGroup,
  resetUserMfa,
  updateAdminUser,
  updateUserGroup,
} from "./api";
import { renderDataAccessSection } from "./dataAccessUI";
import { isGisOnly } from "./dataProfile";
import { renderDiagnosticsSection } from "./diagnosticsUI";
import { renderFeedbackRegister } from "./feedbackRegister";
import { refreshDerivedBadge } from "./derivedDataUI";
import { el } from "./legend";
import { renderMaintenanceSection } from "./maintenanceUI";
import { renderOnboardingSection } from "./onboardingAdmin";
import { renderExternalMembersSection, userOrganisationsControls } from "./orgMemberships";
import { getState, roleAtLeast } from "./state";
import type { UserRole } from "./state";
import { showToast } from "./toast";
import type { AdminUser, AuditLogEntry, Group, TermsAcceptanceList } from "./types";
import { setTeamPickerDisabled, teamPicker } from "./workspaces";

const ROLES = ["view", "configure", "manager", "admin"] as const;

const usersContainer = () => document.getElementById("admin-users-container")!;
const auditContainer = () => document.getElementById("admin-audit-container")!;

/** Wide tables scroll sideways inside the sidebar rather than squashing or
 * being clipped (the sidebar itself never scrolls horizontally). */
function scrollWrap(table: HTMLElement): HTMLElement {
  const wrap = el("div", { className: "admin-table-scroll" });
  wrap.append(table);
  return wrap;
}

// Same "module-level cache, null = not loaded yet, re-render pulls a fresh
// copy on invalidation" pattern as panel.ts's customPackagesCache.
let usersCache: AdminUser[] | null = null;
let groupsCache: Group[] | null = null;
let auditCache: AuditLogEntry[] | null = null;
let auditUsernameFilter = "";
let auditActionFilter = "";

/** A one-line human summary of an audit_log row's detail JSON — a config
 * save's {changes: [...]} gets the "path: old -> new" treatment
 * (staleness.ts's formatChange does the same for the confirm-modal
 * version); anything else just lists its own key=value pairs. */
function formatDetail(entry: AuditLogEntry): string {
  const changes = entry.detail.changes;
  if (Array.isArray(changes) && changes.length > 0) {
    const fmt = (v: unknown) => (typeof v === "object" ? JSON.stringify(v) : String(v));
    return changes.map((c) => `${c.path}: ${fmt(c.old)} → ${fmt(c.new)}`).join("; ");
  }
  const { changes: _omit, ...rest } = entry.detail;
  return Object.entries(rest)
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(", ");
}

function renderUsersSection(root: HTMLElement): void {
  root.append(el("h2", {}, "Users"));

  // The per-user Group <select> needs the full groups list to populate its
  // options, so this section waits on both caches rather than just its own.
  if (usersCache === null || groupsCache === null) {
    root.append(el("p", { className: "caption" }, "Loading users…"));
    if (usersCache === null) {
      void listAdminUsers().then((users) => {
        usersCache = users;
        renderUsersPane();
      });
    }
    if (groupsCache === null) {
      void listAdminGroups().then((groups) => {
        groupsCache = groups;
        renderUsersPane();
      });
    }
    return;
  }

  const currentUsername = getState().currentUser?.username;
  const table = el("table", { className: "detail-table" });
  const headerRow = document.createElement("tr");
  headerRow.append(
    el("th", {}, "Username"),
    el("th", {}, "Display name"),
    el("th", {}, "Email"),
    el("th", {}, "Role"),
    el("th", {}, "Teams"),
    el("th", {}, "Group"),
    el("th", {}, "Active"),
    el("th", {}, "Created"),
  );
  // Multi-organisation membership (orgMemberships.ts): platform admins only
  const canOrgs = roleAtLeast(getState().currentUser, "platform_admin");
  if (canOrgs) headerRow.append(el("th", {}, "Organisations"));
  table.append(headerRow);

  for (const u of usersCache) {
    const tr = document.createElement("tr");
    const isSelf = u.username === currentUsername;
    tr.append(el("td", {}, u.username), el("td", {}, u.display_name));

    // Email (flow plan stage A): used by the Teams / invite / email contact
    // actions. Saved on change; "" clears it.
    const emailInput = el("input", { type: "email", placeholder: "name@org", value: u.email ?? "" }) as HTMLInputElement;
    emailInput.style.width = "13rem";
    emailInput.title = "Saved when you press Enter or click away";
    emailInput.addEventListener("input", () => emailInput.setCustomValidity(""));
    emailInput.addEventListener("keydown", (e) => { if (e.key === "Enter") emailInput.blur(); });
    emailInput.addEventListener("change", () => {
      emailInput.disabled = true;
      updateAdminUser(u.id, { email: emailInput.value.trim() })
        .then(() => {
          // say so: on 27 Oct emails typed here had never been saved (no request reached the server)
          showToast(emailInput.value.trim() ? `Email saved for ${u.display_name}` : `Email cleared for ${u.display_name}`);
          usersCache = null;
          renderUsersPane();
        })
        .catch((err: Error) => {
          emailInput.disabled = false;
          emailInput.setCustomValidity(err instanceof ApiError && err.status === 400 ? "Not a valid email address"
            : `Not saved: ${err.message}`);
          emailInput.reportValidity();
        });
    });
    const emailTd = el("td", {});
    emailTd.append(emailInput);
    tr.append(emailTd);

    const roleSelect = document.createElement("select");
    for (const r of ROLES) {
      const opt = document.createElement("option");
      opt.value = r;
      opt.textContent = r;
      opt.selected = r === u.role;
      roleSelect.append(opt);
    }
    // Can't demote/deactivate yourself out of the admin screen — the
    // backend blocks it too (routers/admin.py's last-active-admin check),
    // this just avoids a round trip to find that out.
    roleSelect.disabled = isSelf;
    roleSelect.addEventListener("change", () => {
      const previous = u.role;
      roleSelect.disabled = true;
      updateAdminUser(u.id, { role: roleSelect.value })
        .then(() => {
          usersCache = null;
          renderUsersPane();
        })
        .catch(() => {
          roleSelect.value = previous;
          roleSelect.disabled = false;
        });
    });
    const roleTd = el("td", {});
    roleTd.append(roleSelect);
    tr.append(roleTd);

    // Teams = workspaces (workspaces.ts); several = a workspace dropdown.
    // Takes effect on the user's next page load.
    const picker = teamPicker(u.teams, (teams) => {
      setTeamPickerDisabled(picker.el, true);
      updateAdminUser(u.id, { teams })
        .then(() => {
          usersCache = null;
          renderUsersPane();
        })
        .catch(() => {
          usersCache = null;
          renderUsersPane();
        });
    });
    const teamTd = el("td", {});
    teamTd.append(picker.el);
    tr.append(teamTd);

    const groupSelect = document.createElement("select");
    const ungroupedOpt = document.createElement("option");
    ungroupedOpt.value = "";
    ungroupedOpt.textContent = "Ungrouped";
    ungroupedOpt.selected = u.group_id == null;
    groupSelect.append(ungroupedOpt);
    for (const g of groupsCache) {
      const opt = document.createElement("option");
      opt.value = String(g.id);
      opt.textContent = g.name;
      opt.selected = u.group_id === g.id;
      groupSelect.append(opt);
    }
    groupSelect.addEventListener("change", () => {
      const previous = u.group_id;
      groupSelect.disabled = true;
      updateUserGroup(u.id, groupSelect.value === "" ? null : Number(groupSelect.value))
        .then(() => {
          usersCache = null;
          renderUsersPane();
        })
        .catch(() => {
          groupSelect.value = previous == null ? "" : String(previous);
          groupSelect.disabled = false;
        });
    });
    const groupTd = el("td", {});
    groupTd.append(groupSelect);
    tr.append(groupTd);

    const activeTd = el("td", {});
    // Users are never deleted: the audit log is append-only (2026-10-09) and
    // references them, so deactivating is the only "remove".
    const activeBtn = el("button", {
      title: u.active
        ? "Blocks sign-in. Users are never deleted: their audit history is kept (the audit log is append-only)."
        : "Allow this user to sign in again.",
    }, u.active ? "Deactivate" : "Reactivate");
    activeBtn.disabled = isSelf;
    activeBtn.addEventListener("click", () => {
      activeBtn.disabled = true;
      updateAdminUser(u.id, { active: !u.active })
        .then(() => {
          usersCache = null;
          renderUsersPane();
        })
        .catch(() => {
          activeBtn.disabled = isSelf;
        });
    });
    activeTd.append(el("span", { className: "caption" }, u.active ? "Active" : "Inactive — "), activeBtn);
    // Two-factor reset (routers/admin.py mfa-reset, audited): a lost phone
    const mfaOn = (u as { mfa_enabled?: boolean }).mfa_enabled;
    if (mfaOn && !isSelf) {
      const resetBtn = el("button", { type: "button", title: "Turn off this user's two-factor sign-in (lost phone). Ends their sessions." }, "Reset 2FA");
      resetBtn.addEventListener("click", () => {
        if (!window.confirm(`Reset two-factor sign-in for ${u.username}? They sign in with just their password until they set it up again.`)) return;
        resetBtn.disabled = true;
        resetUserMfa(u.id)
          .then(() => {
            usersCache = null;
            renderUsersPane();
          })
          .catch(() => {
            resetBtn.disabled = false;
          });
      });
      activeTd.append(" ", resetBtn);
    } else if (mfaOn) {
      activeTd.append(el("span", { className: "caption" }, " · 2FA on"));
    }
    tr.append(activeTd);

    tr.append(el("td", { className: "caption" }, new Date(u.created_at).toLocaleDateString()));
    table.append(tr);
    const orgs = canOrgs ? userOrganisationsControls(u.id, u.username, (u as { client_id?: number }).client_id ?? null, 9) : null;
    if (orgs) {
      const td = el("td", {});
      td.append(orgs.button);
      tr.append(td);
      table.append(orgs.detailRow);
    }
  }
  root.append(scrollWrap(table));

  const form = el("form");
  form.style.display = "flex";
  form.style.flexWrap = "wrap";
  form.style.gap = "0.4rem";
  form.style.alignItems = "center";
  form.style.marginTop = "0.75rem";

  const usernameInput = el("input", { type: "text", placeholder: "Username", required: true }) as HTMLInputElement;
  const displayNameInput = el("input", { type: "text", placeholder: "Display name" }) as HTMLInputElement;
  const passwordInput = el("input", { type: "password", placeholder: "Password", required: true }) as HTMLInputElement;
  const emailNew = el("input", { type: "email", placeholder: "Email (optional)" }) as HTMLInputElement;
  const roleSelectNew = document.createElement("select");
  for (const r of ROLES) {
    const opt = document.createElement("option");
    opt.value = r;
    opt.textContent = r;
    roleSelectNew.append(opt);
  }
  const teamsNew = teamPicker(null);
  const submitBtn = el("button", { type: "submit" }, "Add user");
  const statusEl = el("span", { className: "caption" });

  form.append(usernameInput, displayNameInput, emailNew, passwordInput, roleSelectNew, teamsNew.el, submitBtn, statusEl);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    statusEl.textContent = "";
    submitBtn.disabled = true;
    createAdminUser({
      username: usernameInput.value.trim(),
      display_name: displayNameInput.value.trim(),
      role: roleSelectNew.value,
      password: passwordInput.value,
      teams: teamsNew.value(),
      email: emailNew.value.trim() || undefined,
    })
      .then(() => {
        usersCache = null;
        renderUsersPane();
      })
      .catch(() => {
        statusEl.textContent = "Could not create user — username may already be taken.";
        submitBtn.disabled = false;
      });
  });
  root.append(el("h3", {}, "Add user"), form);
}

function renderGroupsSection(root: HTMLElement): void {
  root.append(el("h2", {}, "Groups"));

  if (groupsCache === null) {
    root.append(el("p", { className: "caption" }, "Loading groups…"));
    void listAdminGroups().then((groups) => {
      groupsCache = groups;
      renderUsersPane();
    });
    return;
  }

  const table = el("table", { className: "detail-table" });
  const headerRow = document.createElement("tr");
  headerRow.append(el("th", {}, "Name"), el("th", {}, "Members"), el("th", {}, ""));
  table.append(headerRow);

  for (const g of groupsCache) {
    const tr = document.createElement("tr");
    const nameInput = el("input", { type: "text", value: g.name }) as HTMLInputElement;
    const saveBtn = el("button", {}, "Save");
    saveBtn.addEventListener("click", () => {
      const name = nameInput.value.trim();
      if (!name || name === g.name) return;
      saveBtn.disabled = true;
      renameAdminGroup(g.id, name)
        .then(() => {
          groupsCache = null;
          usersCache = null; // group_name display would be stale too, if it were shown
          renderUsersPane();
        })
        .catch(() => {
          nameInput.value = g.name;
          saveBtn.disabled = false;
        });
    });
    const deleteBtn = el("button", {}, "Delete");
    deleteBtn.addEventListener("click", () => {
      deleteBtn.disabled = true;
      deleteAdminGroup(g.id)
        .then(() => {
          groupsCache = null;
          usersCache = null; // members of this group fall back to "Ungrouped"
          renderUsersPane();
        })
        .catch(() => {
          deleteBtn.disabled = false;
        });
    });
    const nameTd = el("td", {});
    nameTd.append(nameInput);
    const actionsTd = el("td", {});
    actionsTd.append(saveBtn, deleteBtn);
    tr.append(nameTd, el("td", { className: "caption" }, String(g.member_count)), actionsTd);
    table.append(tr);
  }
  root.append(scrollWrap(table));

  const form = el("form");
  form.style.display = "flex";
  form.style.flexWrap = "wrap";
  form.style.gap = "0.4rem";
  form.style.alignItems = "center";
  form.style.marginTop = "0.75rem";
  const newNameInput = el("input", { type: "text", placeholder: "Group name", required: true }) as HTMLInputElement;
  const submitBtn = el("button", { type: "submit" }, "Add group");
  const statusEl = el("span", { className: "caption" });
  form.append(newNameInput, submitBtn, statusEl);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    statusEl.textContent = "";
    submitBtn.disabled = true;
    createAdminGroup(newNameInput.value.trim())
      .then(() => {
        groupsCache = null;
        renderUsersPane();
      })
      .catch(() => {
        statusEl.textContent = "Could not create group — name may already be taken.";
        submitBtn.disabled = false;
      });
  });
  root.append(el("h3", {}, "Add group"), form);
}

function renderActivitySection(root: HTMLElement): void {
  root.append(el("h2", {}, "Audit log"));
  root.append(el("p", { className: "caption" }, "Recent activity by users of your organisation."));

  const filterRow = el("div");
  filterRow.style.display = "flex";
  filterRow.style.flexWrap = "wrap";
  filterRow.style.gap = "0.4rem";
  filterRow.style.marginBottom = "0.5rem";
  const usernameFilterInput = el("input", {
    type: "text",
    placeholder: "Filter by username",
    value: auditUsernameFilter,
  }) as HTMLInputElement;
  const actionFilterInput = el("input", {
    type: "text",
    placeholder: "Filter by action",
    value: auditActionFilter,
  }) as HTMLInputElement;
  const applyBtn = el("button", {}, "Filter");
  applyBtn.addEventListener("click", () => {
    auditUsernameFilter = usernameFilterInput.value.trim();
    auditActionFilter = actionFilterInput.value.trim();
    auditCache = null;
    renderAuditPane();
  });
  filterRow.append(usernameFilterInput, actionFilterInput, applyBtn);
  root.append(filterRow);

  if (auditCache === null) {
    root.append(el("p", { className: "caption" }, "Loading activity…"));
    void getAuditLog({ username: auditUsernameFilter || undefined, action: auditActionFilter || undefined }).then(
      (entries) => {
        auditCache = entries;
        renderAuditPane();
      },
    );
    return;
  }

  if (auditCache.length === 0) {
    root.append(el("p", { className: "caption" }, "No matching activity."));
    return;
  }

  const table = el("table", { className: "detail-table" });
  const headerRow = document.createElement("tr");
  headerRow.append(
    el("th", {}, "When"),
    el("th", {}, "Who"),
    el("th", {}, "Action"),
    el("th", {}, "Target"),
    el("th", {}, "Detail"),
  );
  table.append(headerRow);
  for (const entry of auditCache) {
    const tr = document.createElement("tr");
    tr.append(
      el("td", { className: "caption" }, new Date(entry.at).toLocaleString()),
      el("td", {}, entry.display_name ?? entry.username ?? "unknown"),
      el("td", {}, entry.action),
      el("td", { className: "caption" }, `${entry.target_type}:${entry.target_id}`),
      el("td", { className: "caption" }, formatDetail(entry)),
    );
    table.append(tr);
  }
  root.append(scrollWrap(table));
}

function renderUsersPane(): void {
  const root = usersContainer();
  root.innerHTML = "";
  // Access requests + invite links (onboardingAdmin.ts)
  renderOnboardingSection(root);
  root.append(document.createElement("hr"));
  renderUsersSection(root);
  root.append(document.createElement("hr"));
  // memberships OF this organisation (orgMemberships.ts)
  renderExternalMembersSection(root);
  root.append(document.createElement("hr"));
  renderGroupsSection(root);
}

/** Terms of use acceptances (routers/terms.py) — platform_admin only: who
 * accepted which version and when, and who hasn't accepted the current one. */
function renderTermsSection(root: HTMLElement): void {
  const section = el("div");
  section.append(el("h2", {}, "Terms of use acceptances"));
  section.append(el("p", { className: "caption" }, "Loading…"));
  root.append(section);
  void getTermsAcceptances()
    .then((data: TermsAcceptanceList) => {
      section.innerHTML = "";
      section.append(el("h2", {}, "Terms of use acceptances"));
      section.append(
        el(
          "p",
          { className: "caption" },
          `Current version ${data.current_version}. ${data.not_accepted.length} active user(s) have not accepted it yet.`,
        ),
      );
      if (data.not_accepted.length > 0) {
        const details = el("details");
        details.append(el("summary", { className: "caption" }, "Not yet accepted"));
        const ul = el("ul", { className: "caption" });
        for (const u of data.not_accepted) {
          ul.append(el("li", {}, `${u.display_name} (${u.username}, ${u.role}${u.client_name ? `, ${u.client_name}` : ""})`));
        }
        details.append(ul);
        section.append(details);
      }
      if (data.acceptances.length === 0) {
        section.append(el("p", { className: "caption" }, "No acceptances recorded yet."));
        return;
      }
      const table = el("table", { className: "detail-table" });
      const head = document.createElement("tr");
      head.append(el("th", {}, "When"), el("th", {}, "Who"), el("th", {}, "Organisation"), el("th", {}, "Version"),
        el("th", {}, "IP"));
      table.append(head);
      for (const a of data.acceptances) {
        const tr = document.createElement("tr");
        tr.append(
          el("td", { className: "caption" }, new Date(a.accepted_at).toLocaleString()),
          el("td", {}, a.display_name ?? a.username ?? "deleted user"),
          el("td", { className: "caption" }, a.client_name ?? ""),
          el("td", { className: "caption" }, a.version),
          el("td", { className: "caption", title: a.user_agent ?? "" }, a.ip ?? ""),
        );
        table.append(tr);
      }
      section.append(scrollWrap(table));
    })
    .catch(() => {
      section.innerHTML = "";
      section.append(el("h2", {}, "Terms of use acceptances"), el("p", { className: "caption" }, "Could not load acceptances."));
    });
}

function renderAuditPane(): void {
  const root = auditContainer();
  root.innerHTML = "";
  renderActivitySection(root);
  // Only on the pass after the audit rows arrived (renderActivitySection
  // re-renders this pane once they load), so it's fetched once per open.
  if (auditCache !== null && roleAtLeast(getState().currentUser, "platform_admin")) {
    root.append(document.createElement("hr"));
    renderTermsSection(root);
  }
}

interface AdminTabSpec {
  tab: string; // data-tab (workspaces.ts ADMIN_TABS)
  button: string;
  container: string;
  minRole: UserRole;
  /** Not reachable for a GIS-view organisation (the data layer 404s it). */
  fullProfileOnly?: boolean;
  render: (root: HTMLElement) => void;
}

const ADMIN_TAB_SPECS: AdminTabSpec[] = [
  { tab: "adminUsers", button: "admin-users-tab-btn", container: "admin-users-container", minRole: "admin",
    render: () => renderUsersPane() },
  { tab: "adminAudit", button: "admin-audit-tab-btn", container: "admin-audit-container", minRole: "admin",
    render: () => {
      auditCache = null; // fresh rows each time the tab is opened
      renderAuditPane();
    } },
  // P1 data access (per-organisation grants, shadow/denied log)
  { tab: "adminAccess", button: "admin-access-tab-btn", container: "admin-access-container", minRole: "platform_admin",
    render: (root) => renderDataAccessSection(root) },
  // Shared-pipeline maintenance: platform_admin only since 2026-10-01
  { tab: "adminMaintenance", button: "admin-maintenance-tab-btn", container: "admin-maintenance-container",
    minRole: "platform_admin", fullProfileOnly: true, render: (root) => renderMaintenanceSection(root) },
  { tab: "adminDiagnostics", button: "admin-diagnostics-tab-btn", container: "admin-diagnostics-container",
    minRole: "platform_admin", render: (root) => renderDiagnosticsSection(root) },
  // Flow plan stage B: curated organisation contact directory (per client)
  { tab: "adminContacts", button: "admin-contacts-tab-btn", container: "admin-contacts-container", minRole: "admin",
    fullProfileOnly: true, render: (root) => renderContactDirectorySection(root) },
  // Page feedback register (right-click "Give feedback on this page…"): reloads on every open
  { tab: "adminFeedback", button: "admin-feedback-tab-btn", container: "admin-feedback-container", minRole: "admin",
    render: (root) => renderFeedbackRegister(root) },
];

/** Called once at bootstrap (see main.ts), before initWorkspaces: shows the
 * Admin workspace's tabs this user may use and hides the rest entirely.
 * Role is fixed for the lifetime of a session (a role change takes effect
 * on next login, same as every other role-gated bit of UI in this app — see
 * api.ts's handleUnauthorized). Each tab renders on first open (so an admin
 * who never opens it fires none of its requests); Audit log reloads on every
 * open. */
export function initAdmin(): void {
  // roleAtLeast, not an exact role check — platform_admin does everything a
  // client-scoped admin does, plus more (found missing here via an actual
  // browser check during Phase 2; see state.ts's ROLE_RANK comment for the
  // same bug in a different spot).
  const user = getState().currentUser;
  const gisOnly = isGisOnly();
  for (const spec of ADMIN_TAB_SPECS) {
    const btn = document.getElementById(spec.button);
    if (!btn) continue;
    const allowed = roleAtLeast(user, spec.minRole) && !(spec.fullProfileOnly && gisOnly);
    btn.style.display = allowed ? "" : "none";
    if (!allowed) continue;
    // stale / missing derived data: a count badge on the Diagnostics tab
    if (spec.tab === "adminDiagnostics") refreshDerivedBadge();
    let rendered = false;
    btn.addEventListener("click", () => {
      if (rendered && spec.tab !== "adminAudit" && spec.tab !== "adminFeedback") return;
      rendered = true;
      spec.render(document.getElementById(spec.container)!);
    });
  }
}
